import { Injectable, computed, signal } from '@angular/core'
import type { IReporteeUser } from '@app/core/models/reportees.model'
import { IDropdownConfig, IDropdownOption } from '@app/shared/models/dropdown.model'
import {
    SHIKSHA_DASHBOARD_SCOPE,
    SHIKSHA_DROPDOWN_CONTENT,
    buildShikshaReporteeScopeValue,
    parseShikshaReporteeScopeValue,
    SHIKSHA_SCOPE_ENTIRE_TEAM,
    SHIKSHA_SCOPE_OPTIONS,
    SHIKSHA_YEAR_OPTIONS,
    getCurrentShikshaFinancialYearLabel,
    getInitialShikshaYearPlaceholder,
} from '@app/features/home/shiksha-components/constants/shiksha-dashboard.constants'

@Injectable({
    providedIn: 'root',
})
export class ShikshaDashboardFiltersService {
    private readonly _teamScopeAvailable = signal<boolean>(true)
    private readonly _reporteesForScope = signal<readonly IReporteeUser[]>([])
    private readonly _yearOptions = signal<readonly string[]>(SHIKSHA_YEAR_OPTIONS)
    readonly yearOptions = this._yearOptions.asReadonly()

    private readonly _selectedScope = signal<string>(SHIKSHA_SCOPE_OPTIONS[0])
    private readonly _selectedYear = signal<string>(getInitialShikshaYearPlaceholder())
    /** After the first non-empty API year list, prefer current FY only on that first hydration. */
    private _appliedFinancialYearOptionsFromApi = false
    private readonly _refreshTick = signal<number>(0)
    private readonly _isRefreshing = signal<boolean>(false)
    private readonly _lastRefreshedAt = signal<Date | null>(null)

    readonly selectedScope = this._selectedScope.asReadonly()
    readonly selectedYear = this._selectedYear.asReadonly()
    readonly refreshTick = this._refreshTick.asReadonly()
    readonly isRefreshing = this._isRefreshing.asReadonly()
    readonly lastRefreshedAt = this._lastRefreshedAt.asReadonly()

    readonly availableScopeOptions = computed(() =>
        this._teamScopeAvailable() ? SHIKSHA_SCOPE_OPTIONS : [SHIKSHA_DASHBOARD_SCOPE.MYSELF],
    )

    readonly scopeDropdownConfig = computed<IDropdownConfig>(() => {
        const baseOptions = this.availableScopeOptions().map(
            (option): IDropdownOption => ({
                id: option,
                value: option,
                label: option,
            }),
        )
        const reporteeOptions: IDropdownOption[] =
            this._teamScopeAvailable() && this._reporteesForScope().length > 0
                ? this._reporteesForScope().map((reportee) => {
                      const value = buildShikshaReporteeScopeValue(reportee.user_id)
                      const label = (reportee.user_name?.trim() || reportee.user_id).trim()
                      return {
                          id: value,
                          value,
                          label,
                      }
                  })
                : []
        return {
            placeholder: SHIKSHA_DROPDOWN_CONTENT.scopePlaceholder,
            options: [...baseOptions, ...reporteeOptions],
            enableSearch: reporteeOptions.length > 5,
        }
    })

    /**
     * When false, scope is fixed to personal contribution (user has no reportees / no team roll-up).
     * Call from Shiksha shell when reportees eligibility is known.
     */
    setTeamScopeAvailable(available: boolean): void {
        this._teamScopeAvailable.set(available)
        if (!available) {
            this._reporteesForScope.set([])
            const selected = this._selectedScope()
            if (
                selected === SHIKSHA_SCOPE_ENTIRE_TEAM ||
                parseShikshaReporteeScopeValue(selected) != null
            ) {
                this._selectedScope.set(SHIKSHA_DASHBOARD_SCOPE.MYSELF)
            }
        }
    }

    /** Reportee rows for the View Scope list (names + ids). Cleared when not on Shiksha or user has no team. */
    setReporteesForScope(reportees: readonly IReporteeUser[]): void {
        const sorted = [...reportees].sort((reporteeA, reporteeB) =>
            (reporteeA.user_name ?? '').localeCompare(reporteeB.user_name ?? '', undefined, {
                sensitivity: 'base',
            }),
        )
        this._reporteesForScope.set(sorted)

        const selected = this._selectedScope()
        const reporteeId = parseShikshaReporteeScopeValue(selected)
        if (reporteeId && !sorted.some((reportee) => reportee.user_id === reporteeId)) {
            this._selectedScope.set(SHIKSHA_SCOPE_OPTIONS[0])
        }
    }

    readonly yearDropdownConfig = computed<IDropdownConfig>(() => ({
        placeholder: SHIKSHA_DROPDOWN_CONTENT.yearPlaceholder,
        options: this.yearOptions().map(
            (year): IDropdownOption => ({
                id: year,
                value: year,
                label: year,
            }),
        ),
    }))

    setSelectedScope(scope: string): void {
        if (scope?.trim()) {
            this._selectedScope.set(scope)
        }
    }

    setSelectedYear(year: string): void {
        if (year?.trim()) {
            this._selectedYear.set(year)
        }
    }

    setAvailableFinancialYears(financialYears: readonly number[]): void {
        const yearOptions = this.toFinancialYearLabels(financialYears)
        if (!yearOptions.length) {
            return
        }

        this._yearOptions.set(yearOptions)

        const currentFyLabel = getCurrentShikshaFinancialYearLabel()
        const newestLabel = yearOptions[yearOptions.length - 1]
        const preferred = yearOptions.find((label) => label === currentFyLabel) ?? newestLabel

        if (!this._appliedFinancialYearOptionsFromApi) {
            this._appliedFinancialYearOptionsFromApi = true
            this._selectedYear.set(preferred)
            return
        }

        if (!yearOptions.includes(this._selectedYear())) {
            this._selectedYear.set(preferred)
        }
    }

    requestRefresh(): void {
        this.markRefreshInProgress()
        this._refreshTick.update((value) => value + 1)
    }

    markRefreshInProgress(): void {
        this._isRefreshing.set(true)
    }

    markRefreshComplete(): void {
        this._lastRefreshedAt.set(new Date())
        this._isRefreshing.set(false)
    }

    private toFinancialYearLabels(financialYears: readonly number[]): readonly string[] {
        const uniqueYears = Array.from(
            new Set(
                financialYears
                    .filter((year) => Number.isFinite(year))
                    .map((year) => Math.trunc(year)),
            ),
        ).sort((yearA, yearB) => yearA - yearB)

        return uniqueYears.map((startYear) => {
            const endYear = (startYear + 1) % 100
            return `${startYear}-${String(endYear).padStart(2, '0')}`
        })
    }
}
